import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Stacked Bar Chart component for test types
export default function StackedBarChart({ data, labels, title = "Stacked Bar Chart" }) {
  if (!data || Object.keys(data).length === 0 || !labels || labels.length === 0) {
    return (
      <div style={{ width: '100%', height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>No data available</p>
      </div>
    );
  }

  // Generate distinct colors for each test type
  const colors = [
    'rgba(255, 99, 132, 0.8)', 'rgba(54, 162, 235, 0.8)', 'rgba(255, 205, 86, 0.8)',
    'rgba(75, 192, 192, 0.8)', 'rgba(153, 102, 255, 0.8)', 'rgba(255, 159, 64, 0.8)',
    'rgba(199, 199, 199, 0.8)', 'rgba(83, 102, 255, 0.8)', 'rgba(255, 99, 255, 0.8)',
    'rgba(99, 255, 132, 0.8)', 'rgba(255, 206, 84, 0.8)', 'rgba(54, 162, 200, 0.8)',
    'rgba(255, 99, 180, 0.8)', 'rgba(180, 99, 255, 0.8)', 'rgba(99, 180, 255, 0.8)',
    'rgba(255, 180, 99, 0.8)', 'rgba(180, 255, 99, 0.8)', 'rgba(99, 255, 180, 0.8)'
  ];

  const borderColors = colors.map(color => color.replace('0.8', '1'));

  // Create datasets for each test type
  const datasets = Object.keys(data).map((testType, index) => ({
    label: testType,
    data: data[testType],
    backgroundColor: colors[index % colors.length],
    borderColor: borderColors[index % borderColors.length],
    borderWidth: 1,
  }));

  const chartData = {
    labels: labels,
    datasets: datasets,
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 12,
          font: {
            size: 11,
          },
          padding: 15,
          usePointStyle: true,
        },
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 16,
          weight: 'bold',
        },
        color: '#333',
        padding: 20,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#666',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const testType = context.dataset.label;
            const count = context.parsed.y;
            return `${testType}: ${count} tests`;
          },
          footer: function(tooltipItems) {
            const total = tooltipItems.reduce((sum, item) => sum + item.parsed.y, 0);
            return `Total: ${total} tests`;
          }
        }
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#666',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1,
        },
        ticks: {
          color: '#666',
          font: {
            size: 11,
          },
          callback: function(value) {
            return Math.floor(value); // Show only whole numbers
          },
        },
      },
    },
    animation: {
      duration: 2000,
      easing: 'easeInOutQuart',
    },
  };

  return (
    <div style={{ width: '100%', height: '400px' }}>
      <Bar data={chartData} options={options} />
    </div>
  );
}
